import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr } from "@/lib/format";
import { VideoCameraIcon, UploadSimpleIcon, XIcon, CheckCircleIcon } from "@phosphor-icons/react";

/**
 * <VideoInput />
 * Campo input URL + botón "Subir" para cargar un MP4/WebM directamente a R2 y
 * autocompletar la URL. Ideal para el CMS del Landing (Hero video, play button, etc.).
 *
 * Props:
 *  - value: URL actual
 *  - onChange: (nuevaUrl: string) => void
 *  - folder: carpeta destino en R2 (default "landing")
 *  - maxMB: tamaño máximo permitido (default 100)
 *  - testid: prefijo para data-testids
 */
export default function VideoInput({ value = "", onChange, folder = "landing", maxMB = 150, testid = "video-input" }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const pickFile = () => inputRef.current?.click();

  // Subida por chunks (4 MB): evita los límites/timeouts del proxy con videos grandes.
  const uploadChunked = async (file) => {
    const { data: init } = await api.post("/uploads/chunked/init", {
      filename: file.name,
      content_type: file.type || "video/mp4",
      total_size: file.size,
      folder,
      kind: "video",
    });
    const chunkSize = init.chunk_size || 4 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    try {
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
        const fd = new FormData();
        fd.append("index", String(i));
        fd.append("chunk", blob, `part-${i}`);
        let attempt = 0;
        // reintento automático por chunk (redes móviles inestables)
        for (;;) {
          try {
            await api.post(`/uploads/chunked/${init.upload_id}/chunk`, fd, { timeout: 120000 });
            break;
          } catch (err) {
            attempt += 1;
            if (attempt >= 3) throw err;
            await new Promise((r) => setTimeout(r, 1500 * attempt));
          }
        }
        setProgress(Math.round(((i + 1) / totalChunks) * 95)); // 95% = partes enviadas
      }
      setProgress(97); // ensamblando + subiendo a R2
      const { data } = await api.post(`/uploads/chunked/${init.upload_id}/complete`, {}, { timeout: 300000 });
      setProgress(100);
      return data;
    } catch (err) {
      api.delete(`/uploads/chunked/${init.upload_id}`).catch(() => {});
      throw err;
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`El archivo supera ${maxMB} MB. Comprime el video (1080p es suficiente para el hero).`);
      e.target.value = "";
      return;
    }
    if (!file.type.startsWith("video/")) {
      toast.error("Selecciona un archivo de video (MP4, WebM o MOV)");
      e.target.value = "";
      return;
    }
    if (/\.(mov|qt)$/i.test(file.name) || file.type === "video/quicktime") {
      toast.warning("Los videos .MOV de iPhone/iPad pueden no reproducirse en todos los navegadores. Recomendado: exportar como MP4 (H.264).", { duration: 8000 });
    }
    setUploading(true);
    setProgress(1);
    try {
      const data = await uploadChunked(file);
      onChange && onChange(data.url);
      toast.success(`Video subido (${(file.size / (1024 * 1024)).toFixed(1)} MB)`);
    } catch (er) {
      toast.error(apiErr(er));
    } finally {
      setUploading(false);
      setProgress(0);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <VideoCameraIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange && onChange(e.target.value)}
            placeholder="https://... o pulsa Subir"
            className="w-full bg-black/40 border border-white/10 rounded-md pl-8 pr-3 py-2 text-sm text-white/90 focus:border-agro-green focus:outline-none"
            data-testid={`${testid}-url`}
          />
          {value && (
            <button
              type="button"
              onClick={() => onChange && onChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-red-400"
              title="Borrar URL"
              data-testid={`${testid}-clear`}
            >
              <XIcon size={12} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={pickFile}
          disabled={uploading}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold whitespace-nowrap transition ${
            uploading
              ? "bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30"
              : "bg-agro-green text-black hover:bg-agro-green-hover"
          }`}
          data-testid={`${testid}-upload-btn`}
        >
          {uploading ? (
            <>
              <span className="w-3 h-3 border-2 border-agro-yellow border-t-transparent rounded-full animate-spin" />
              {progress > 0 ? `${progress}%` : "Subiendo…"}
            </>
          ) : (
            <>
              <UploadSimpleIcon size={14} weight="bold" /> Subir
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime,video/*"
          onChange={handleUpload}
          className="hidden"
          data-testid={`${testid}-file`}
        />
      </div>
      {uploading && progress > 0 && (
        <div className="h-1 bg-white/10 rounded overflow-hidden">
          <div className="h-full bg-agro-green transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      {value && !uploading && (
        <div className="text-[10px] text-agro-green flex items-center gap-1">
          <CheckCircleIcon size={10} weight="fill" /> Video listo — se reproduce en loop silenciado
        </div>
      )}
    </div>
  );
}
